import {
	ButtonWithLoading,
	MesfModal,
	TimeService,
	getMomentTz,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Button, Grid } from "@material-ui/core";
import { DateTimePicker } from "@mui/x-date-pickers";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { LazyLoading } from "../../../../controls/LazyLoading";
import { Process } from "../../../caster/models/ProcessGanttChart";
import { upsertProcess as upsertCasterProcess } from "../../../caster/repositories/ProductionRepository";
import { upsertProcess as upsertEAFProcess } from "../../../eaf/eaf-processes/repositories/EAFRepository";
import { useCasterDashboardContext } from "../../CasterDashboardContext";

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	refreshData: Function;
	comparisonDate: Date | null;
};

export const UpsertProcess = ({
	show,
	onHide,
	refreshData,
	comparisonDate,
}: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isNew, setIsNew] = useState(false);
	const [isNotClosed, setIsNotClosed] = useState(false);
	const [title, setTitle] = useState("");
	const { state, actions } = useCasterDashboardContext();
	const [warning, setWarning] = useState(false);
	const [endStartWarning, setEndStartWarning] = useState(false);
	const { shiftInfo } = useShiftNavigator();
	const [previousProcess, setPreviousProcess] = useState<any | null>(null);
	const [nextProcess, setNextProcess] = useState<any | null>(null);
	const [processCreationErrorMessage, setProcessCreationErrorMessage] =
		useState<string>("");

	const isCaster =
		state.selectedAsset?.id && Number(state.selectedAsset?.id) == 13;

	const startLabel = isCaster ? "Ladle Open" : "Start";
	const endLabel = isCaster ? "Ladle Close" : "End";

	const findPreviousAndNextProcess = (processes: any[], timestamp: Date) => {
		let previousProcess: any = null;
		let nextProcess: any = null;
		// First, find the previous process based on the timestamp
		for (const process of processes) {
			if (process.end <= timestamp) {
				if (!previousProcess || process.end > previousProcess.end) {
					previousProcess = process;
				}
			}
		}
		// Use the end of the previous process as the new base time
		const baseTime = previousProcess ? previousProcess.end : timestamp;

		// Then, find the next process based on the end time of the previous process
		for (const process of processes) {
			if (process.processStart > baseTime) {
				if (!nextProcess || process.processStart < nextProcess.processStart) {
					nextProcess = process;
				}
			}
		}
		return { previousProcess, nextProcess };
	};

	const {
		setValue,
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<Process>({
		defaultValues: {},
	});

	const CurrentStart = get(shiftInfo, "CurrentStart", null);
	const CurrentShiftEnd = get(shiftInfo, "CurrentEnd", null);

	const handleDateChange = (fieldOnChange: any) => (date: any) => {
		const currentStart = moment.utc(CurrentStart).local();
		const dateToCompare = moment.utc(date).local();

		fieldOnChange(date);
		if (!dateToCompare.isSame(currentStart)) {
			setWarning(true);
		} else {
			setWarning(false);
		}
	};

	const onSubmit: SubmitHandler<Process> = async (data: Process) => {
		setIsSubmitLoading(true);
		const start = data.Start;
		const end = data.End;
		const { selectedProcess, selectedAsset } = state;
		const processId = data.ProcessId === 0 ? null : data.ProcessId;

		const assetId =
			selectedProcess && selectedProcess.ProcessId !== 0
				? selectedProcess.AssetId
				: Number(selectedAsset?.id);

		if (!assetId) {
			actions.setErrorMessage("Asset ID is not defined.");
			setIsSubmitLoading(false);
			return;
		}

		const isCaster = assetId === 13;

		const response = isCaster
			? await upsertCasterProcess(processId, start, end)
			: await upsertEAFProcess(processId, start, end, true, assetId);

		if (response.ok) {
			actions.setNotificationMessage(
				`Process was ${isNew ? "created" : "edited"} successfully`,
			);
			await refreshData();
		} else {
			actions.setErrorMessage(response?.message);
		}
		setIsSubmitLoading(false);
		onHide(true);
	};

	const open = watch("Start");
	const close = watch("End");

	useEffect(() => {
		if (Math.abs(moment(open).diff(moment(close), "hours")) > 3) {
			setEndStartWarning(true);
		} else {
			setEndStartWarning(false);
		}
	}, [open, close]);

	useEffect(() => {
		setWarning(false);
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				setProcessCreationErrorMessage("");
				const process = state.selectedProcess;
				if (process === undefined || process === null) return;
				if (process.ProcessId === 0) {
					// new process
					setTitle(`NEW
						${state.selectedAsset?.name.toUpperCase()}
						 PROCESS`);
					setIsNew(true);

					if (state.selectedAsset && comparisonDate) {
						const { previousProcess, nextProcess } = findPreviousAndNextProcess(
							state.selectedAsset.processes,
							comparisonDate,
						);
						setPreviousProcess(previousProcess);
						setNextProcess(nextProcess);
						let startTime;
						let endTime;
						if (previousProcess && previousProcess.end) {
							startTime = moment(previousProcess.end)
								.add(2, "minutes")
								.toDate();
						} else {
							startTime = CurrentStart ? CurrentStart : moment().toDate();
						}

						if (nextProcess && nextProcess.processStart) {
							endTime = moment(nextProcess.processStart)
								.subtract(2, "minutes")
								.toDate();
						} else {
							endTime = CurrentShiftEnd
								? moment(CurrentShiftEnd).add(1, "hour").toDate()
								: moment().toDate();
						}

						if (
							previousProcess &&
							previousProcess.end &&
							nextProcess &&
							nextProcess.processStart
						) {
							const timeGap = moment(nextProcess.processStart).diff(
								moment(previousProcess.end),
								"minutes",
							);

							if (timeGap < 4) {
								setProcessCreationErrorMessage(
									"The space between processes is too small to create a new process in the selected position.",
								);
								setValue("Start", previousProcess.end);
								setValue("End", nextProcess.processStart);
								setIsLoading(false);
								return;
							}
						}

						setValue("ProcessId", 0);
						setValue("Start", startTime);
						setValue("End", endTime);
					}
				} else {
					// edit process
					setTitle(
						`EDIT 
							${state.selectedProcess?.AssetId == 13 ? "CASTER" : "EAF"}
							PROCESS`,
					);
					setIsNew(false);
					setValue("ProcessId", process.ProcessId);
					setValue("Start", process.ProcessStart);
					setValue("End", process.End);
				}
				if (process.End === null) setIsNotClosed(true);
				setIsLoading(false);
			})();
		} else {
			reset();
			setIsNotClosed(false);
		}
	}, [show]);

	useEffect(() => {
		if (isNotClosed) {
			setValue("End", new Date());
		}
	}, [isNotClosed]);

	return (
		<>
			<Grid container>
				<Grid item>
					<MesfModal
						maxWidth="md"
						open={show}
						handleClose={() => {
							onHide(true);
						}}
						id="upsertProcess"
						title={title}
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content style={{ padding: "15px 30px" }}>
								{isLoading && (
									<Grid
										container
										justifyContent="center"
										alignItems="center"
										style={{ minHeight: "300px" }}
									>
										<LazyLoading />
									</Grid>
								)}
								{!isLoading && (
									<>
										<Grid container spacing={1}>
											<Grid item xs={12} md={6}>
												<Controller
													name="Start"
													control={control}
													rules={{
														required: "The Start field is required",
														validate: {
															notAfterEnd: (value) => {
																const endValue = watch("End");
																if (!endValue) return true;
																return (
																	moment(value).isBefore(moment(endValue)) ||
																	"Start must be before End"
																);
															},
															notBeforePreviousEnd: (value) => {
																if (isNew) return true;
																const start = state.selectedProcess?.ProcessId
																	? state.selectedProcess?.Start
																	: previousProcess?.end;
																return (
																	moment(value).isSameOrAfter(moment(start)) ||
																	"Start cannot be less than the End of the Previous Process"
																);
															},
															notAfterNextStart: (value) => {
																if (!nextProcess?.start) return true;
																return (
																	moment(value).isSameOrBefore(
																		moment(nextProcess.start),
																	) ||
																	"Start cannot be greater than the Start of the Next Process"
																);
															},
														},
													}}
													render={({ field }) => (
														<DateTimePicker
															label={startLabel}
															timezone={TimeService.getInstance().getServerTimeZone()}
															format="MM/DD/YYYY HH:mm"
															disabled={!!processCreationErrorMessage}
															timeSteps={{ minutes: 1 }}
															value={moment(field.value) as any}
															onChange={handleDateChange(field.onChange)}
															inputRef={field.ref}
															slotProps={{
																textField: {
																	variant: "outlined",
																	error: !!errors.Start,
																},
															}}
														/>
													)}
												/>
												{errors.Start && (
													<p style={{ color: "red", fontSize: 12 }}>
														{errors.Start?.message}
													</p>
												)}
											</Grid>
										</Grid>
										<Grid container spacing={1}>
											<Grid item xs={12} md={6} style={{ marginTop: 10 }}>
												<Controller
													name="End"
													control={control}
													rules={{
														required: !isNotClosed
															? "The End field is required"
															: false,
														validate: {
															notBeforeStart: (value) => {
																const startValue = watch("Start");
																if (!startValue) return true;
																return (
																	moment(value).isAfter(moment(startValue)) ||
																	"End must be after Start"
																);
															},
															notAfterNextStart: (value) => {
																if (!nextProcess?.start) return true;
																return (
																	moment(value).isSameOrBefore(
																		moment(nextProcess.start),
																	) ||
																	"End cannot be greater than the Start of the Next Process"
																);
															},
														},
													}}
													render={({ field }) => (
														<DateTimePicker
															label={endLabel}
															timezone={TimeService.getInstance().getServerTimeZone()}
															format="MM/DD/YYYY HH:mm"
															disabled={!!processCreationErrorMessage}
															timeSteps={{ minutes: 1 }}
															value={moment(field.value) as any}
															onChange={field.onChange}
															inputRef={field.ref}
															slotProps={{
																textField: {
																	variant: "outlined",
																	error: !!errors.End,
																},
															}}
														/>
													)}
												/>
												{errors.End && (
													<p style={{ color: "red", fontSize: 12 }}>
														{errors.End?.message}
													</p>
												)}
											</Grid>
											{processCreationErrorMessage && (
												<p style={{ color: "red", fontSize: 18 }}>
													{processCreationErrorMessage}
												</p>
											)}
											{/* <Grid item xs={12} md={6} style={{ marginTop: 20 }}>
												<FormControlLabel
													control={
														(
															<Checkbox
																{...register("IsOpen")}
																color="primary"
																checked={isNotClosed}
																onChange={(event) =>
																	setIsNotClosed(event.target.checked)
																}
															/>
														) as any
													}
													label="Ladle is not Closed"
												/>
											</Grid> */}
										</Grid>
									</>
								)}
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="default"
											onClick={() => onHide(true)}
										>
											Cancel
										</Button>
									</Grid>
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										{!!processCreationErrorMessage ? (
											<Button
												fullWidth
												variant="contained"
												color="primary"
												type="submit"
												disabled={!!processCreationErrorMessage}
											>
												Save
											</Button>
										) : (
											<ButtonWithLoading
												fullWidth
												variant="contained"
												color="primary"
												type="submit"
												isLoading={isSubmitLoading}
											>
												Save
											</ButtonWithLoading>
										)}
									</Grid>
								</Grid>
							</MesfModal.Actions>
						</form>
					</MesfModal>
				</Grid>
			</Grid>
		</>
	);
};
